import React, { Suspense } from 'react';
// REACT-ROUTER
import { Switch, Route, Redirect } from 'react-router-dom';
// COMPONENTS
import AppFetch from 'fetch/AppFetch';
import LoginFetch from 'fetch/LoginFetch';
import AppConfig from 'appConfig';
import ForgotPassword from 'components/Login/ForgotPassword';
import LoginWrapper from 'components/Login/LoginWrapper';
import NotFound from 'components/NotFound';
import Forbidden from 'components/Forbidden';
// HELPERS
import { retry } from 'lib/AppHelpers';
// CONSTANTS
import { moduleConfig } from 'lib/config/appConstants';
import ProtectedRoute from './ProtectedRoute';

// declare Asynchronously loading versions of the components used in the router
const AlternateAddressesTableWrapper = React.lazy(() =>
  retry(() => import('components/AlternateAddressesView/AlternateAddressesTableWrapper'))
);
const DotDetailsDashboardWrapper = React.lazy(() =>
  retry(() => import('Views/DotDetailsDashboard/DotDetailsDashboardWrapper'))
);
const EbResultsWrapper = React.lazy(() =>
  retry(() => import('components/EbResults/EbResultsWrapper'))
);
const EbSearchWrapper = React.lazy(() => retry(() => import('Views/EbSearch/EbSearchWrapper')));
const FavoritesWrapper = React.lazy(() => retry(() => import('Views/Favorites/FavoritesWrapper')));
const IndustriesWrapper = React.lazy(() =>
  retry(() => import('components/Industries/IndustriesWrapper'))
);
const LocationWrapper = React.lazy(() =>
  retry(() => import('components/LocationLookup/LocationWrapper'))
);
const OSHADetailsDashboardWrapper = React.lazy(() =>
  retry(() => import('Views/OSHADetailsDashboard/OSHADetailsDashboardWrapper'))
);
const PcResultsWrapper = React.lazy(() =>
  retry(() => import('components/PcResults/PcResultsWrapper'))
);
const PcSearchWrapper = React.lazy(() =>
  retry(() => import('components/PcSearch/PcSearchWrapper'))
);
const WHDDetailsTableWrapper = React.lazy(() =>
  retry(() => import('components/WhdDetailsTable/WHDDetailsTableWrapper'))
);
const MyAccountWrapper = React.lazy(() => retry(() => import('Views/MyAccount/MyAccountWrapper')));
// Zywave Details Dashboard
const DetailsDashboardWrapper = React.lazy(() =>
  retry(() => import('Views/DetailsDashboard/DetailsDashboardWrapper'))
);
// Event Alerts
const AlertsWrapper = React.lazy(() => retry(() => import('Views/Alerts/AlertsWrapper')));
// Event AlertsDetails
const AlertsDetailsWrapper = React.lazy(() =>
  retry(() => import('Views/AlertsDetails/AlertsDetailsWrapper'))
);
// Track Exports
const TrackExportsWrapper = React.lazy(() =>
  retry(() => import('Views/TrackExports/TrackExportsWrapper'))
);
// Statistics Pages
const EbStatsPageWrapper = React.lazy(() => retry(() => import('Views/EbStatistics')));
const PcStatsPageWrapper = React.lazy(() => retry(() => import('Views/PcStatistics')));

// Broker Dashboard
const BrokerDashboard = React.lazy(() => retry(() => import('Views/EbBrokerDashboard')));
// SavedSearches
const SavedSearchesWrapper = React.lazy(() =>
  retry(() => import('Views/SavedSearches/SavedSearchesWrapper'))
);

const Logout = () =>
  LoginFetch.logout()
    .then(() => {
      AppFetch.deleteToken();
      AppFetch.deleteUserInfo();
      AppFetch.deleteAgencyUser();
      AppFetch.deleteZywaveUserFeatureAgencies();

      // Open login screen
      window.location = AppConfig.logoutUrl;
      return Promise.resolve();
    })
    .catch((error) => {
      if (this.props.onAppError) {
        this.props.onAppError(error);
      }
    });

function Routes() {
  return (
    <Suspense fallback={<div></div>}>
      <Switch>
        {/* COMMON ROUTES */}
        <Route exact path="/forgotpassword" component={ForgotPassword} />
        <Route exact path="/edge/login" component={LoginWrapper} />
        <Route exact path="/edge/forgotpassword" component={ForgotPassword} />
        <ProtectedRoute exact path={'/edge/:module/favorites'} component={FavoritesWrapper} />
        <ProtectedRoute
          exact
          path="/edge/alternate-addresses/:module/:muns"
          component={AlternateAddressesTableWrapper}
        />
        <Route exact path="/edge/logout" component={Logout} />
        <ProtectedRoute exact path="/edge/myaccount/:module" component={MyAccountWrapper} />
        <ProtectedRoute exact path={'/edge/:module/event_alerts'} component={AlertsWrapper} />
        {/* TODO: verify where below route is used */}
        <ProtectedRoute
          exact
          path="/edge/event_alerts_details/:muns/:subscription_ids"
          component={AlertsDetailsWrapper}
        />
        <ProtectedRoute exact path="/edge/:module/track_exports" component={TrackExportsWrapper} />
        {/* COMMON ROUTE REDIRECTS */}
        <Redirect from="/login" to="/edge/login" />
        <Redirect from="/forgotpassword" to="/edge/forgotpassword" />
        <Redirect from="/favorites" to="/edge/favorites" />
        <Redirect from="/event_alerts" to="/edge/event_alerts" />
        <Redirect
          from="/event_alerts_details/:muns/:subscription_ids"
          to="/edge/event_alerts_details/:muns/:subscription_ids"
        />
        <Redirect from="/alternate-addresses/:muns" to="/edge/alternate-addresses/pc/:muns" />
        <Redirect from="/edge/alternate-addresses/:muns" to="/edge/alternate-addresses/pc/:muns" />
        {/* PC ROUTES */}
        <ProtectedRoute exact path={moduleConfig.PC.home} component={PcSearchWrapper} />
        <ProtectedRoute exact path="/edge/results/pc" component={PcResultsWrapper} />
        <ProtectedRoute exact path="/edge/industries" component={IndustriesWrapper} />
        <ProtectedRoute exact path="/edge/location_lookup" component={LocationWrapper} />
        <ProtectedRoute
          exact
          path="/edge/osha-details/:muns"
          component={OSHADetailsDashboardWrapper}
        />
        <ProtectedRoute
          exact
          path="/edge/osha-details2/:muns"
          component={OSHADetailsDashboardWrapper}
        />
        <ProtectedRoute exact path="/edge/whd-details/:muns" component={WHDDetailsTableWrapper} />
        <ProtectedRoute
          exact
          path="/edge/dot-details/:muns"
          component={DotDetailsDashboardWrapper}
        />
        <ProtectedRoute exact path="/edge/statistics/pc" component={PcStatsPageWrapper} />

        <ProtectedRoute exact path="/edge/results/dashboard" component={DetailsDashboardWrapper} />
        <ProtectedRoute
          exact
          path="/edge/results/dashboard/:muns"
          component={DetailsDashboardWrapper}
        />
        {/* Forbidden */}
        <Route exact path="/edge/forbidden" component={Forbidden} />

        <Redirect exact from="/industries" to="/edge/industries" />
        <Redirect exact from="/location_lookup" to="/edge/location_lookup" />
        <Redirect exact from="/osha-details/:muns" to="/edge/osha-details/:muns" />
        <Redirect exact from="/whd-details/:muns" to="/edge/whd-details/:muns" />
        <Redirect exact from="/dot-details/:muns" to="/edge/dot-details/:muns" />
        {/* EB Routes */}
        <ProtectedRoute exact path="/edge/eb/broker/:msid" component={BrokerDashboard} />
        <ProtectedRoute exact path="/edge/eb/savedsearches" component={SavedSearchesWrapper} />
        <ProtectedRoute exact path="/edge/pc/savedsearches" component={SavedSearchesWrapper} />
        <ProtectedRoute exact path={moduleConfig.EB.home} component={EbSearchWrapper} />
        <ProtectedRoute exact path="/edge/results/eb" component={EbResultsWrapper} />
        <ProtectedRoute exact path="/edge/statistics/eb" component={EbStatsPageWrapper} />

        <Redirect exact from="/edge/results" to="/edge/results/pc" />
        <Redirect exact from="/results" to="/edge/results/pc" />
        <Redirect exact from="/edge" to={moduleConfig.PC.home} />
        <Redirect exact from="/" to={moduleConfig.PC.home} />
        {/* Not Found */}
        <Route path="*" component={NotFound} />
      </Switch>
    </Suspense>
  );
}

export default Routes;
